import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HighlightcardItems } from '../../../shared/interfaces/highlightcard-item.interface';
import { CommonModule } from '@angular/common';
import { HighlightcardComponent } from '../highlightcard/highlightcard.component';
import HighlightCardType from '../../../shared/enums/highlightcard.type.enums';
import { Colors } from '../../../shared/enums/shared.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { ImagesService } from '../../../services/images.service';

@Component({
  selector: 'hdi-highlightcard-list',
  standalone: true,
  imports: [CommonModule, HighlightcardComponent],
  templateUrl: './highlightcard-list.component.html',
  styleUrls: ['./highlightcard-list.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class HighlightcardListComponent implements OnChanges {
  @Input() items: HighlightcardItems[] = [];
  @Input() columns: string = '2';
  @Input() caasData?: CaasComponent;

  public readonly HighlightCardType = HighlightCardType;
  public type: HighlightCardType = HighlightCardType.HIGHLIGHT;
  private images: { src: string; alt: string; title: string }[] = [];

  constructor(private imagesService: ImagesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
    }
  }

  setCaasContent() {
    this.setCaasImage();
    this.setCaasHighlightcardItems();
    this.setCaasColumns();
  }

  setCaasColumns() {
    this.columns = this.caasData?.formData.st_columns?.value?.identifier ?? '2';
  }

  setCaasImage() {
    if (!this.caasData) return [];
    this.images =
      this.caasData.formData.st_items?.value.flatMap((item) => {
        return (
          item.formData.st_images?.value.map((image) => {
            if (image) {
              return {
                src: image.formData.st_image?.value.url ?? '',
                alt:
                  this.imagesService.getAltTextById(
                    image.formData.st_image?.value.identifier ?? ''
                  ) ?? '',
                title: image.formData.st_image_title?.value ?? '',
              };
            }
            return { src: '', alt: '', title: '' };
          }) ?? []
        );
      }) ?? [];
    return this.images;
  }

  setCaasHighlightcardItems() {
    this.items =
      this.caasData!.formData.st_items?.value.map(
        (item: CaasComponent, index: number) => {
          return {
            inhalt: [
              {
                headline: item.formData.st_header?.value,
                content: this.removeListContent(
                  item.formData.st_bodytext?.value
                ),
                checklist: this.extractListContent(
                  item.formData.st_bodytext?.value
                ),
              },
            ],
            icontype: item.formData.st_badge_icon?.value ?? 'user',
            iconBgColor:
              item.formData.st_badge_color?.value.identifier === 'turquoise'
                ? Colors.LIGHT_BLUE
                : item.formData.st_badge_color?.value.identifier === 'blue'
                  ? Colors.BLUE
                  : Colors.GREEN,
            cta: false,
            ctaLabel: '',
            ctaType: LinkType.DEFAULT,
            ctaSrc: '',
            image: false,
            imageSrc: [this.images[index]?.src],
            imageAlt: [this.images[index]?.alt],
            imageTitle: [this.images[index]?.title],
          };
        }
      ) ?? [];
  }

  extractListContent(htmlString?: string | null) {
    if (!htmlString) return undefined;
    const ulRegex = /<ul[^>]*>(.*?)<\/ul>/gs;
    const matches = [];
    let match;
    let cleanMatch;

    while ((match = ulRegex.exec(htmlString)) !== null) {
      const cleanRegex = /<li[^>]*>(.*?)(?:<\/li>|<ul[^>]*>)/gs;
      while ((cleanMatch = cleanRegex.exec(match[1])) !== null) {
        matches.push(cleanMatch[1]);
      }
    }

    if (matches.length > 0) this.type = HighlightCardType.CHECKLIST;
    return matches
      .filter((match) => match.trim().length > 0)
      .map((match) => ({ li: `<p>${match}</p>` }));
  }

  removeListContent(htmlString?: string | null) {
    if (!htmlString) return undefined;
    // Regular expression to match content between <p><ul> and </ul></p>
    const regex = /<p>\s*<ul[^>]*>.*?<\/ul>\s*<\/p>/gs;

    // Replace matched content with an empty string
    return htmlString.replace(regex, '');
  }
}
